import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Package, MapPin, User, Truck, CheckCircle, KeyRound, Send, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  registered:       { label: 'Registered',        color: 'bg-slate-100 text-slate-700',     icon: Package },
  loaded:           { label: 'Loaded on Bus',     color: 'bg-blue-100 text-blue-700',       icon: Truck },
  in_transit:       { label: 'In Transit',        color: 'bg-amber-100 text-amber-700',     icon: Truck },
  at_destination:   { label: 'At Destination',    color: 'bg-emerald-100 text-emerald-700', icon: MapPin },
  delivered:        { label: 'Delivered',         color: 'bg-emerald-600 text-white',       icon: CheckCircle },
  past_destination: { label: 'Past Destination',  color: 'bg-red-600 text-white',           icon: AlertCircle },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return format(parseISO(iso), 'dd MMM yyyy HH:mm'); } catch { return iso; }
};

const LuggageTimeline = ({ open, onClose, luggageRef }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpHint, setOtpHint] = useState(null);   // mock-only: surface generated OTP

  const load = async () => {
    if (!luggageRef || !token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/enterprise/luggage/${luggageRef}/timeline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setData(null);
      setOtp('');
      setOtpHint(null);
      load();
    }
  }, [open, luggageRef]);

  const sendOtp = async () => {
    setOtpSending(true);
    try {
      const r = await axios.post(`${API_URL}/enterprise/luggage/${luggageRef}/pickup/start`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const mockOtp = r.data?.otp_record?.payload?.otp;
      setOtpHint(mockOtp || null);
      toast.success(`OTP sent to ${r.data?.sent_to}` + (mockOtp ? ` (mock: ${mockOtp})` : ''));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const confirmOtp = async () => {
    if (!otp || otp.length < 4) {
      toast.error('Enter the 6-digit OTP');
      return;
    }
    setOtpVerifying(true);
    try {
      await axios.post(`${API_URL}/enterprise/luggage/${luggageRef}/pickup/confirm`,
        { otp }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pickup verified — luggage marked as DELIVERED');
      setOtp('');
      setOtpHint(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid OTP');
    } finally {
      setOtpVerifying(false);
    }
  };

  const currentMeta = STATUS_META[data?.current_status] || STATUS_META.registered;
  const CurIcon = currentMeta.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="luggage-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Package className="h-5 w-5 text-indigo-600" />
            Luggage Timeline · <span className="font-mono">{luggageRef}</span>
          </DialogTitle>
          <DialogDescription>
            Full chain of custody, sender and receiver details, plus secure OTP-based pickup.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-2 py-4">
            {[1, 2, 3].map(i => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5 py-2">
            {/* Header summary */}
            <div className="flex items-center justify-between flex-wrap gap-2 p-3 rounded-lg bg-slate-50 border">
              <div>
                <p className="text-xs text-slate-500">Route</p>
                <p className="font-semibold text-slate-800">{data.origin || '—'} → {data.destination || '—'}</p>
                {data.bus_registration && (
                  <p className="text-xs text-slate-500">Bus {data.bus_registration} · {data.route_name || ''}</p>
                )}
              </div>
              <Badge className={currentMeta.color}>
                <CurIcon className="h-3 w-3 mr-1" /> {currentMeta.label}
              </Badge>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><User className="h-3 w-3" /> Sender</p>
                <p className="font-medium">{data.sender_name || '—'}</p>
                <p className="text-xs text-slate-500">{data.sender_phone || '—'}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><User className="h-3 w-3" /> Receiver</p>
                <p className="font-medium">{data.receiver_name || 'Self'}</p>
                <p className="text-xs text-slate-500">{data.receiver_phone || '—'}</p>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <p className="text-sm font-semibold mb-2 text-slate-700">Chain of Custody</p>
              {(!data.events || data.events.length === 0) ? (
                <p className="text-sm text-slate-500 italic">No events recorded yet.</p>
              ) : (
                <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3" data-testid="luggage-timeline-events">
                  {data.events.map((e, idx) => {
                    const meta = STATUS_META[e.status] || { label: e.status, color: 'bg-slate-100 text-slate-700', icon: Clock };
                    const Icon = meta.icon;
                    return (
                      <li key={idx} className="ml-4">
                        <div className="absolute -left-[9px] mt-1 w-4 h-4 bg-white border-2 border-indigo-500 rounded-full" />
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={meta.color} variant="outline">
                            <Icon className="h-3 w-3 mr-1" /> {meta.label}
                          </Badge>
                          <span className="text-xs text-slate-500">{fmtTime(e.timestamp)}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-0.5">
                          {e.user_name || 'System'}{e.location ? ` · ${e.location}` : ''}
                        </p>
                        {e.notes && <p className="text-xs text-slate-500 mt-0.5">{e.notes}</p>}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* OTP Pickup */}
            {data.current_status !== 'delivered' && (
              <div className="p-4 border-2 border-dashed border-indigo-200 rounded-lg bg-indigo-50/40">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">
                  <KeyRound className="h-4 w-4 text-indigo-600" /> Secure Pickup (OTP)
                </p>
                <p className="text-xs text-slate-600 mb-3">
                  Send a one-time code to the receiver, then enter it here to release the luggage.
                  This logs an immutable delivery event.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={sendOtp}
                    disabled={otpSending}
                    variant="outline"
                    size="sm"
                    data-testid="luggage-otp-send"
                  >
                    <Send className="h-3 w-3 mr-1" />
                    {otpSending ? 'Sending…' : 'Send OTP to receiver'}
                  </Button>
                  <Input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit code"
                    className="w-32 font-mono tracking-widest"
                    data-testid="luggage-otp-input"
                  />
                  <Button
                    onClick={confirmOtp}
                    disabled={otpVerifying || otp.length < 4}
                    size="sm"
                    data-testid="luggage-otp-confirm"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {otpVerifying ? 'Verifying…' : 'Confirm Pickup'}
                  </Button>
                </div>
                {otpHint && (
                  <p className="text-[11px] text-amber-600 mt-2">
                    Mock mode · generated OTP: <span className="font-mono font-bold">{otpHint}</span>
                  </p>
                )}
              </div>
            )}

            {data.current_status === 'delivered' && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-emerald-700">
                <CheckCircle className="h-4 w-4" /> Luggage has been delivered.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="luggage-timeline-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LuggageTimeline;
